<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="5">
                    <rkm-form-date-picker
                        range
                        :range-inline="false"
                        start-label="Período de"
                        end-label="Até"
                        v-model="model.periodo"
                        :start-validations="validations.periodo.start"
                        :end-validations="validations.periodo.end"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        label="Data de nascimento"
                        v-model="model.dataNascimento"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-select
                        :options="exames"
                        label="Exames"
                        show-default-option
                        text-field="exameNome"
                        v-model="model.exameId"
                        value-field="exameId"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :repository="repositories.paciente"
                        :search-fields="fields.paciente"
                        :text-field="(item) => item.cidadao.nome"
                        label="Paciente"
                        v-model="model.paciente"
                        uppercase
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-input
                        label="Prontuário"
                        v-model="model.prontuario"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-button
                        :label="false"
                        color="primary"
                        class="float-left"
                        @click="$refs.Form.validate(search)"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="resetForm()"
                        class="mr-2 float-left"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-table
            v-if="notSaveLancamentos"
            :items="notSaveLancamentos || data.model.lancamentos"
            :fields="fields.tables.lancamentos"
            :tbody-tr-attr="handleRowAttr"
            ref="Table"
            sort-by="data"
            hide-filters
        >
            <template v-slot:cell(pacienteNome)="row">
                <rkm-button
                    class="mr-2"
                    color="secondary"
                    small
                    v-rkm-popover.hover.left="
                        {
                            title: 'Dados cadastrais',
                            html: true,
                            content:
                                '<b>Prontuário:</b> ' + (row.item.prontuario || '') + '<br/>' +
                                '<b>Data de Nascimento:</b> ' + row.item.dataNascimento + '<br/>' +
                                '<b>Nome da Mãe:</b> ' + row.item.nomeMae + '<br/>'
                        }
                    "
                >
                    <rkm-icon icon="user" />
                </rkm-button>

                <span v-if="!row.item.sequencialDesconhecido">
                    {{row.item.pacienteNome}}

                    <template v-if="row.item.nomeSocial">
                        <template v-if="row.item.nomeSocial">
                            <strong>({{ row.item.nomeSocial }})</strong>
                        </template>

                        <template v-else>
                            <strong>({{ row.item.nomeSocial }})</strong>
                        </template>
                    </template>
                </span>

                <span
                    v-else
                    class="text-primary"
                    v-rkm-popover.hover.top="
                        {
                            title: 'Características físicas',
                            content: row.item.caracteristicasFisicas
                        }
                    "
                >
                    {{row.item.pacienteNome}}
                </span>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    @click.stop="$router.push({ name: 'exame-imagem-alteracao-resultado-lancamento-edit', params: { id: item.idPaciente } })"
                    @imprimir="imprimir"
                    color="primary"
                    title="Editar resultado"
                    small
                >
                    <rkm-icon icon="file-signature" />

                </rkm-button>
            </template>
        </rkm-table>

        <rkm-row class="mt-2">
            <rkm-col>
                <p class="m-0">
                 Linhas em <span class="warning">amarelo</span> representam exames com urgência.
                </p>
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>

// Mixins.
import { repository, error, object, data, stringCase } from '@/mixins'

// Enuns.
import statusFechamento from '@/enums/exame-imagem/fechamento/status'

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

export default {
    created() {

        this.repositories = {
            alteracaoResultado: this.getConectaSusRepository('exameImagem.alteracaoResultadoLancamento'),
            impressao: this.getConectaSusRepository('impressao.exameImagem'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
        }

        this.load()
    },
    computed: {
        fields() {

            return {
                tables: {
                    lancamentos: [
                        { name: 'data', key: 'data', label: 'Data agendada', searchable: true, sortable: true },
                        { name: 'pacienteNome', key: 'pacienteNome', label: 'Paciente', searchable: true, sortable: true },
                        { name: 'dataNascimento', key: 'dataNascimento', label: 'Data nascimento', searchable: true, sortable: true },
                        { name: 'medicoSolicitante', key: 'medicoSolicitante', label: 'Médico', searchable: true, sortable: true },
                        { name: 'dataPedido', key: 'dataPedido', label: 'Data do pedido', searchable: true, sortable: true },
                        { name: 'exameNome', key: 'exameNome', label: 'Exame', searchable: true, sortable: true },
                        { name: 'actions', key: 'actions', label: 'Ações', class: 'col-min text-center' },
                    ]
                },
                paciente: [
                    { key: 'cidadao.nome', searchable: true },
                ],
            }
        },
        validations() {
            return {
                periodo: {
                    start: { required: true },
                    end: { required: true },
                }
            }
        },
        exames(){

            let exameOrigem = []

            if (this.model.periodo.start && this.model.periodo.end){

                this.resetExames()

                const start = moment(this.model.periodo.start).subtract(1, 'day')
                const end = moment(this.model.periodo.end).add(1, 'day')

                this.data.model.lancamentos.filter(u => moment(u.data, 'DD/MM/YYYY').isBetween(start, end))
                    .map(exame => {

                        if (exameOrigem) {

                            let verifyExame = exameOrigem.find(u => u.exameId === exame.exameId)

                            if (!verifyExame) {

                                exameOrigem.push({

                                    exameId: exame.exameId,
                                    exameNome: exame.exameNome,
                                })
                            } else {

                                this.resetExames()
                            }
                        }
                    })

                exameOrigem.sort((a,b) => (a.exameNome > b.exameNome) ? 1 : ((b.exameNome > a.exameNome) ? -1 : 0));
            }

            return exameOrigem
        },
    },
    data: () => ({
        notSaveLancamentos: null,
        model: {
            prontuario: null,
            periodo: {
                start: null,
                end: null,
            },
            paciente: null,
            exameId: null,
            unidadeId: null,
            medicoId: null,
            dataNascimento: null,
            urgente: false,
            statusId: null,
        },
        additionals: {},
        enums:{
            statusFechamento,
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.model.periodo.start = this.data.model.periodo.start
            this.model.periodo.end = this.data.model.periodo.end
        },
        search() {

            this.setLoading()

            this.repositories.alteracaoResultado.datatable(this.getSearchParams())
                .then(({ data }) => this.notSaveLancamentos = data)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getSearchParams() {

            const params = this.clone(this.model)

            this.formateDates(params.periodo, ['start', 'end'], 'DD/MM/YYYY')
            this.formateDates(params, ['dataNascimento'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(params)
        },
        resetExames(){

            this.model.exameId = null
        },
        resetForm() {

            this.model.prontuario =  null
            this.model.paciente =  null
            this.model.exameId =  null
            this.model.unidadeId =  null
            this.model.medicoId =  null
            this.model.dataNascimento =  null
            this.model.statusId =  null

            this.$nextTick()
                .then(() => {

                    this.setDefaultValues()
                    this.$refs.Form.resetValidator()
                })
        },
         setDefaultValues() {

            this.model.periodo.start = this.data.model.periodo.start
            this.model.periodo.end = this.data.model.periodo.end

            this.resetDatatable()
        },
        resetDatatable(){

            this.notSaveLancamentos = null
        },
        imprimir(item) {

            this.setLoading()

            this.repositories.impressao.lancamento(item.idPaciente)
                .then(response => {

                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .finally(() => this.unsetLoading())
        },
        handleRowAttr(item, type) {

            if (type === 'row') {
                if (item.urgente) {
                    return {
                        style: `
                            border-left: 10px solid #ffc107;
                            background-color: #ffc10715;
                        `
                    }
                }
            }
        },
    },
    mixins: [repository, error, object, data, stringCase],
    props: {
        data: { type: Object, required: true }
    }
}
</script>

<style scoped>
    .warning {
        color: var(--warning);
    }
</style>